import React, { useState, useEffect } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
} from "@mui/material";
import AxiosInstance from "../../services/networkServices";
import ActionFilter from "../../components/ActionFilter";
import { makeStyles } from "@mui/styles";
import Spinner from "../../components/Spinner";
import AddIcon from '@mui/icons-material/Add';
import PageLayout from "../../components/PageLayout";

const useStyles = makeStyles((theme) => ({
  root: {
    height: "90vh",
    display: "flex",
    flexDirection: "column",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "24px",
  },
  tableContainer: {
    flex: 1,
    overflowY: "auto",
    minHeight: "calc(100vh - 205px)",
    maxHeight: "calc(100vh - 205px)",
    boxShadow: "none !important",
  },
  tableCell: {
    fontSize: {
      xs: "14px !important",
      sm: "15px !important",
      md: "15px !important",
      lg: "16px !important",
      xl: "16px !important",
    },
    color: "#000 !important",
    fontWeight: "600 !important",
    padding: {
      xs: "10px 5px !important",
      sm: "15px 10px !important",
      md: "15px 10px !important",
      lg: "20px 15px !important",
      xl: "20px 15px !important",
    },
    backgroundColor: "#e0e0e0",
  },
  tableBody: {
    fontSize: {
      xs: "12px !important",
      sm: "13px !important",
      md: "14px !important",
      lg: "15px !important",
      xl: "15px !important",
    },
    color: "#333 !important",
    padding: {
      xs: "10px 5px !important",
      sm: "10px !important",
      md: "10px !important",
      lg: "10px 15px !important",
      xl: "10px 15px !important",
    },
  },
  table: {
    border: "1px solid rgba(224, 224, 224, 1)",
    minWidth: "100%",
    minHeight: "100%",
  },
  stickyHeader: {
    position: "sticky",
    top: "-1px",
    backgroundColor: theme.palette.background.paper,
    zIndex: 1,
  },
  actionsContainer: {
    display: "flex",
    justifyContent: "flex-end",
    padding: "15px 0",
  },
}));

/**
 * `value` is `Schema.Types.Mixed` on the backend. Most settings are plain
 * strings, but the typed ones (rap/karaoke availability, face verification,
 * gameplay weights & thresholds) store objects. Rendering an object as a
 * React child throws and blanks the whole app, so everything non-string is
 * serialized before it hits the DOM.
 */
const isStructuredValue = (value) => value !== null && typeof value === "object";

const formatSettingValue = (value) => {
  if (value === null || value === undefined) return "—";
  if (typeof value === "string") return value;
  if (isStructuredValue(value)) return JSON.stringify(value, null, 2);
  return String(value);
};

const AdminSettings = () => {
  const classes = useStyles();
  const [adminSettings, setAdminSettings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedSetting, setSelectedSetting] = useState(null);
  const [newSetting, setNewSetting] = useState({
    key: '',
    value: '',
    description: ''
  });
  
  useEffect(() => {
    fetchAdminSettings();
  }, []);

  const fetchAdminSettings = async () => {
    try {
      setLoading(true);
      const response = await AxiosInstance.get("/admin/settings/list");
      if (response.data.status) {
        const settings = response.data?.data?.settings;
        setAdminSettings(Array.isArray(settings) ? settings : []);
      } else {
        console.error("Failed to fetch admin settings:", response.data.message);
      }
    } catch (error) {
      console.error("Error fetching admin settings:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (index) => {
    const setting = adminSettings[index];
    setIsEditMode(true);
    setSelectedSetting(setting);
    setNewSetting({
      key: setting.key,
      // The TextField needs a string; objects are shown read-only (see below).
      value: formatSettingValue(setting.value),
      description: setting.description ?? ""
    });
    setOpenDialog(true);
  };

  // The generic /admin/settings/update endpoint only accepts string values.
  // Structured settings are owned by their dedicated pages (Match & Energy,
  // Rap, Karaoke, Face Verification); saving them here would overwrite the
  // object with a string and break those features, so they're read-only.
  const isStructuredSelected =
    isEditMode && selectedSetting && isStructuredValue(selectedSetting.value);

  const handleDelete = async (index) => {
    const setting = adminSettings[index];
    try {
      setLoading(true);
      const response = await AxiosInstance.delete(`/admin/settings/delete/${setting.key}`);
      if (response.data.status) {
        fetchAdminSettings();
      } else {
        console.error("Failed to delete setting:", response.data.message);
      }
    } catch (error) {
      console.error("Error deleting setting:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setIsEditMode(false);
    setSelectedSetting(null);
    setNewSetting({
      key: '',
      value: '',
      description: ''
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setIsEditMode(false);
    setSelectedSetting(null);
    setNewSetting({
      key: '',
      value: '',
      description: ''
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewSetting(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async () => {
    if (isStructuredSelected) return;
    try {
      setLoading(true);
      let response;
      
      if (isEditMode) {
        response = await AxiosInstance.put("/admin/settings/update", newSetting);
      } else {
        response = await AxiosInstance.post("/admin/settings/create", newSetting);
      }

      if (response.data.status) {
        fetchAdminSettings();
        handleCloseDialog();
      } else {
        console.error(`Failed to ${isEditMode ? 'update' : 'create'} setting:`, response.data.message);
      }
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} setting:`, error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageLayout
      title="Admin Settings"
      loading={loading}
      actions={
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleCreate}
          sx={{
            textTransform: 'none',
            fontSize: {
              xs: '12px',
              sm: '13px',
              md: '14px',
              lg: '15px',
              xl: '16px'
            }
          }}
        >
          Create Setting
        </Button>
      }
    >
      <Box className={classes.root}>
        <Box
          sx={{
            padding: "15px 15px 0 15px",
            borderRadius: "6px",
            boxShadow:
              "rgba(0, 0, 0, 0.05) 0 6px 24px 0, rgba(0, 0, 0, 0.08) 0 0 0 1px",
          }}
        >
          <TableContainer component={Paper} className={classes.tableContainer}>
            <Table className={classes.table}>
              <TableHead>
                <TableRow className={classes.stickyHeader}>
                  <TableCell className={classes.tableCell}>Key</TableCell>
                  <TableCell className={classes.tableCell}>Value</TableCell>
                  <TableCell className={classes.tableCell}>Description</TableCell>
                  <TableCell className={classes.tableCell}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {adminSettings.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={4}
                      className={classes.tableBody}
                      sx={{ textAlign: "center" }}
                    >
                      No settings found
                    </TableCell>
                  </TableRow>
                ) : (
                  adminSettings.map((setting, index) => (
                    <TableRow key={setting.key ?? index}>
                      <TableCell className={classes.tableBody}>
                        {setting.key}
                      </TableCell>
                      <TableCell className={classes.tableBody}>
                        {isStructuredValue(setting.value) ? (
                          <Box
                            component="pre"
                            sx={{
                              m: 0,
                              fontFamily: "monospace",
                              fontSize: "12px",
                              whiteSpace: "pre-wrap",
                              wordBreak: "break-word",
                              maxHeight: "160px",
                              overflowY: "auto",
                            }}
                          >
                            {formatSettingValue(setting.value)}
                          </Box>
                        ) : (
                          formatSettingValue(setting.value)
                        )}
                      </TableCell>
                      <TableCell className={classes.tableBody}>
                        {setting.description}
                      </TableCell>
                      <TableCell className={classes.tableBody}>
                        <ActionFilter
                          onEdit={() => handleEdit(index)}
                          onDelete={() => handleDelete(index)}
                          index={index}
                          notification
                        />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        <Dialog
          open={openDialog}
          onClose={handleCloseDialog}
          maxWidth="sm"
          fullWidth
          sx={{
            "& .MuiDialog-paper": {
              width: "100%",
              maxWidth: "500px",
            },
          }}
        >
          <DialogTitle
            sx={{
              padding: "30px 0 15px",
              textAlign: "center",
              fontWeight: 700,
            }}
          >
            {isEditMode ? 'Edit Setting' : 'Create New Setting'}
          </DialogTitle>
          <DialogContent>
            <Box mt={2}>
              <TextField
                fullWidth
                label="Key"
                name="key"
                value={newSetting.key}
                onChange={handleInputChange}
                disabled={isEditMode}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Value"
                name="value"
                value={newSetting.value}
                onChange={handleInputChange}
                disabled={Boolean(isStructuredSelected)}
                multiline={Boolean(isStructuredSelected)}
                maxRows={isStructuredSelected ? 12 : undefined}
                helperText={
                  isStructuredSelected
                    ? "This is a structured setting managed from its own admin page (Match & Energy, Rap, Karaoke, Face Verification, or Mobile). It can't be edited here."
                    : undefined
                }
                inputProps={
                  isStructuredSelected
                    ? { style: { fontFamily: "monospace", fontSize: "12px" } }
                    : undefined
                }
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Description"
                name="description"
                value={newSetting.description}
                onChange={handleInputChange}
                multiline
                rows={3}
              />
            </Box>
            <Box className={classes.actionsContainer}>
              <Button
                sx={{
                  border: "1px solid #888",
                  color: "#888",
                  borderRadius: "6px",
                  fontSize: "14px",
                  marginRight: "15px",
                  "&:hover": {
                    backgroundColor: "#b9b9b9",
                    color: "#222",
                    borderColor: "#b9b9b9",
                  },
                }}
                onClick={handleCloseDialog}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                disabled={
                  !newSetting.key || !newSetting.value || Boolean(isStructuredSelected)
                }
              >
                {isEditMode ? 'Update' : 'Create'} Setting
              </Button>
            </Box>
          </DialogContent>
        </Dialog>
      </Box>
    </PageLayout>
  );
};

export default AdminSettings;

